/**
 * SearchPanel — 접이식 조회 폼 패널 (Card + inline Form).
 *
 * 【역할】
 * - 상단 조회 영역을 Card 로 감싸고 inline Form 으로 Form.Item 들을 나열.
 * - 우측 상단 토글(Up/Down) 로 body 접기/펼치기.
 * - [조회] / [초기화] 버튼 내장.
 *
 * 【주요 기능】
 * - 외부 form 을 받거나 내부에서 useForm 으로 자동 생성.
 * - onSearch: getFieldsValue() 를 전달해 호출, onReset: resetFields() 호출 후 콜백.
 * - 초기 접힘 상태 제어.
 *
 * 【Props】
 * - title?: string — 패널 타이틀 (기본 LBL_SEARCH_PANEL).
 * - children: ReactNode — Form.Item 들.
 * - onSearch?: (values) => void — 조회 버튼 클릭 시 호출.
 * - onReset?: () => void — 초기화 버튼 클릭 시 호출 (내부 resetFields 후).
 * - initialCollapsed?: boolean — 초기 접힘 여부 (기본 false).
 * - form?: FormInstance — 외부 제어 form. 생략 시 내부 생성.
 * - loading?: boolean — 조회 버튼 로딩 상태.
 *
 * 【사용 예시】
 * ```tsx
 * <SearchPanel form={form} onSearch={handleSearch} loading={loading}>
 *   <Form.Item name="keyword"><Input /></Form.Item>
 * </SearchPanel>
 * ```
 */
import { useState, ReactNode } from 'react'
import { Card, Form, Button, Space, FormInstance, theme as antdTheme } from 'antd'
import { SearchOutlined, ReloadOutlined, DownOutlined, UpOutlined } from '@ant-design/icons'
import { useLabel } from '@/framework/useLabel'

export interface SearchPanelProps {
  /** 패널 타이틀 (기본: LBL_SEARCH_PANEL). */
  title?: string
  /** Form.Item 들. */
  children: ReactNode
  /** 조회 버튼 클릭 시 호출 (현재 form 값 전달). */
  onSearch?: (values: Record<string, unknown>) => void
  /** 초기화 버튼 클릭 시 호출 (내부 resetFields 선행). */
  onReset?: () => void
  /** 초기 접힘 여부 (기본 false). */
  initialCollapsed?: boolean
  /** 외부 제어용 Form 인스턴스. 생략 시 내부에서 자동 생성. */
  form?: FormInstance
  /** 조회 버튼 로딩 상태. */
  loading?: boolean
}

/**
 * SearchPanel — 조회 폼 + 접이식 토글 + [조회/초기화] 버튼 패널.
 * @param props - 패널 옵션 (`SearchPanelProps`)
 * @returns Card 로 래핑된 검색 폼 엘리먼트
 */
export default function SearchPanel(props: SearchPanelProps) {
  const { t } = useLabel()
  const [collapsed, setCollapsed] = useState(props.initialCollapsed ?? false)
  const [innerForm] = Form.useForm()
  const form = props.form ?? innerForm
  const { token } = antdTheme.useToken()

  /**
   * 조회 버튼 클릭 핸들러.
   * - 현재 form 의 모든 필드 값을 수집해 onSearch 콜백에 전달.
   * - htmlType="submit" 으로 Enter 키 입력 시에도 동일 경로로 호출됨.
   */
  const handleSearch = () => {
    const values = form.getFieldsValue()
    props.onSearch?.(values)
  }

  /**
   * 초기화 버튼 클릭 핸들러.
   * - form 의 모든 필드를 초기값으로 reset 한 뒤 사용자 onReset 후처리(예: 재조회) 호출.
   */
  const handleReset = () => {
    form.resetFields()
    props.onReset?.()
  }

  return (
    <Card
      size="small"
      title={props.title ?? t('LBL_SEARCH_PANEL', '조회')}
      extra={
        <Button
          type="text"
          icon={collapsed ? <DownOutlined /> : <UpOutlined />}
          onClick={() => setCollapsed((v) => !v)}
        />
      }
      style={{ marginBottom: token.marginSM }}
      styles={{ body: { display: collapsed ? 'none' : 'block' } }}
    >
      <Form form={form} layout="inline" onFinish={handleSearch}>
        {props.children}
        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SearchOutlined />}
              loading={props.loading}
            >
              {t('BTN_SEARCH', '조회')}
            </Button>
            <Button icon={<ReloadOutlined />} onClick={handleReset}>
              {t('BTN_RESET', '초기화')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  )
}
